/*
 * Copyright © 2021 Firecrest Clinical, Ltd.
 * All rights reserved.
*/

import {NodeType, Schema} from 'prosemirror-model';
import {EditorState, Transaction} from 'prosemirror-state';
import {liftListItem, wrapInList} from 'prosemirror-schema-list';
import {Command} from 'prosemirror-commands';
import {isNodeActive} from '../../../helpers';

class ListItem {
    /**
     * Is bullet list.
     */
    isBulletList = false;

    /**
     * Default constructor.
     * 
     * @param isBulletList Is bullet list.
     */
    constructor(isBulletList = false) {
        this.isBulletList = isBulletList;
    }

    /**
     * Get type.
     * 
     * @param schema Schema.
     * @returns NodeType.
     */
    getType(schema: Schema): NodeType {
        return this.isBulletList ? schema.nodes['bullet_list'] : schema.nodes['ordered_list'];
    }

    /**
     * Toggle function.
     * 
     * @returns Command.
     */
    toggle(): Command {
        return (state: EditorState, dispatch?: (tr: Transaction) => void): boolean => {

            const {schema} = state;

            const type = this.getType(schema);
            if (!type) {
                return false;
            }

            if (this.isActive(state)) {
                return liftListItem(schema.nodes.list_item)(state, dispatch);
            }

            return wrapInList(type)(state, dispatch);
        };
    }

    /**
     * Is command active.
     * 
     * @param state EditorState.
     * @returns Boolean.
     */
    isActive(state: EditorState): boolean {
        const {schema} = state;

        const type = this.getType(schema);
        if (!type) {
            return false;
        }

        return isNodeActive(state, type);
    }

    /**
     * Does command can execute.
     * 
     * @param state EditorState.
     * @returns Boolean.
     */
    canExecute(state: EditorState): boolean {
        return this.toggle()(state);
    }
}

export default ListItem;
